import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { RootState, AppData, PageType, UserRole } from 'app/types'
import { Button, Icon, Spinner } from 'components'
import { navigateAsync } from 'actions/appActions'
import { fetchAnalyticsDashboard, fetchAnalyticsDashboards } from 'actions/adminActions'
import { AnalyticsDashboard } from 'views/AdminPage/Views/AnalyticsDashboard'
import * as fnc from 'helpers/fnc'

interface DashboardViewProps {}

interface DashboardTile {
    id: string
    title: string
    subtitle?: string
    icon: string
    color: string
    onClick: () => void
    isNew?: boolean
    count?: number
}

export function DashboardView(props: DashboardViewProps) {
    const dispatch = useAppDispatch()
    const user = useAppSelector((state: RootState) => state.user.data)
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const config = useAppSelector((state: RootState) => state.app.config)
    const [userApps, setUserApps] = useState<AppData[]>([])
    const [recentPages, setRecentPages] = useState([])
    const [dashboards, setDashboards] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (user && user.apps) {
            // Get full app data from user's app IDs
            const apps = user.apps
                .map(appId => config.apps?.find(app => app.meta.id === appId))
                .filter(app => app != null)
                .slice(0, 8) // Limit to 8 apps for clean grid
            setUserApps(apps)
        }
        setLoading(false)
    }, [user, config])

    useEffect(() => {
        // Fetch analytics dashboards for the user's apps
        const fetchDashboards = async () => {
            if (userApps.length > 0) {
                try {
                    const dashboardPromises = userApps.map(app => 
                        dispatch(fetchAnalyticsDashboards({
                            appId: app.meta.id,
                            organizationId: app.organizationId
                        }))
                    )
                    const results = await Promise.all(dashboardPromises)
                    const allDashboards = results.flatMap(result => result.payload?.dashboards || [])
                    setDashboards(allDashboards)
                } catch (error) {
                    console.error('Error fetching dashboards:', error)
                }
            }
        }

        fetchDashboards()
    }, [userApps, dispatch])

    const handleAppClick = (app: AppData) => {
        dispatch(navigateAsync({ 
            app, 
            pageType: '', 
            options: { path: { baseRoute: null } } 
        }))
    }

    const handleAdminClick = (app: AppData) => {
        dispatch(navigateAsync({ 
            options: { dataLink: app.meta.link, path: { baseRoute: 'admin' } }
        }))
    }

    const handleCreateApp = () => {
        dispatch(navigateAsync({ 
            options: { dataLink: 'apps', path: { baseRoute: 'admin' } }
        }))
    }

    const handleAnalytics = () => {
        if (userApps.length > 0) {
            dispatch(navigateAsync({ 
                options: { dataLink: userApps[0].meta.link, extraLink: 'analytics', path: { baseRoute: 'admin' } }
            }))
        }
    }

    const quickActions: DashboardTile[] = [
        {
            id: 'create-app',
            title: 'Create New App',
            subtitle: 'Start a new project',
            icon: 'fas fa-plus',
            color: '#6366f1',
            onClick: handleCreateApp
        },
        {
            id: 'manage-media',
            title: 'Manage Media',
            subtitle: 'Upload and organize media files',
            icon: 'fas fa-images',
            color: '#8b5cf6',
            onClick: () => dispatch(navigateAsync({ options: { dataLink: 'media', path: { baseRoute: 'admin' } } }))
        },
        {
            id: 'manage-users',
            title: 'Manage Users',
            subtitle: 'User accounts and permissions',
            icon: 'fas fa-users',
            color: '#06b6d4',
            onClick: () => dispatch(navigateAsync({ options: { dataLink: 'users', path: { baseRoute: 'admin' } } }))
        }
    ]

    if (loading) {
        return <div className="dashboard-loading">
            <Spinner />
        </div>
    }

    return (
        <div className="dashboard-view">
            <div className="dashboard-container">
                {/* Header */}
                <div className="dashboard-header">
                    <div className="dashboard-header-content">
                        <h1 className="dashboard-title">
                            Welcome back, {user?.name || 'Admin'}
                        </h1>
                        <p className="dashboard-subtitle">
                            Here's what's happening with your projects today
                        </p>
                    </div>
                    <div className="dashboard-header-actions">
                        <Button 
                            icon="fas fa-plus" 
                            onClick={handleCreateApp}
                            large
                        >
                            Create New App
                        </Button>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="dashboard-section">
                    <h2 className="dashboard-section-title">Quick Actions</h2>
                    <div className="quick-actions-grid">
                        {quickActions.map(action => (
                            <div 
                                key={action.id}
                                className="quick-action-tile"
                                onClick={action.onClick}
                                style={{ '--tile-color': action.color } as React.CSSProperties}
                            >
                                <div className="quick-action-icon">
                                    <Icon name={action.icon} />
                                </div>
                                <div className="quick-action-content">
                                    <h3>{action.title}</h3>
                                    <p>{action.subtitle}</p>
                                </div>
                                <div className="quick-action-arrow">
                                    <Icon name="fas fa-arrow-right" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Your Apps */}
                <div className="dashboard-section">
                    <div className="dashboard-section-header">
                        <h2 className="dashboard-section-title">Your Apps</h2>
                        <Button 
                            tertiary
                            icon="fas fa-th-large"
                            onClick={() => dispatch(navigateAsync({ options: { dataLink: 'apps', path: { baseRoute: 'admin' } } }))}
                        >
                            View All
                        </Button>
                    </div>
                    
                    {userApps.length > 0 ? (
                        <div className="apps-grid">
                            {userApps.map(app => (
                                <div key={app.meta.id} className="app-tile">
                                    <div className="app-tile-header">
                                        <div className="app-tile-icon">
                                            <Icon name="fas fa-building" />
                                        </div>
                                        <div className="app-tile-menu">
                                            <button 
                                                className="app-tile-menu-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleAdminClick(app)
                                                }}
                                            >
                                                <Icon name="fas fa-cog" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="app-tile-content" onClick={() => handleAppClick(app)}>
                                        <h3 className="app-tile-title">{app.meta.name}</h3>
                                        <p className="app-tile-description">
                                            {app.meta.description || 'No description available'}
                                        </p>
                                        <div className="app-tile-stats">
                                            <div className="app-stat">
                                                <Icon name="fas fa-file-alt" />
                                                <span>{app.pages?.length || 0} Pages</span>
                                            </div>
                                            <div className="app-stat">
                                                <Icon name="fas fa-vector-square" />
                                                <span>{app.floorplans?.length || 0} Plans</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="app-tile-footer">
                                        <span className="app-tile-updated">
                                            Updated {fnc.formatDate(app.meta.dateModified)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <Icon name="fas fa-rocket" />
                            </div>
                            <h3>Get started with your first app</h3>
                            <p>Create an app to start building your digital sales center</p>
                            <Button icon="fas fa-plus" onClick={handleCreateApp} large>
                                Create Your First App
                            </Button>
                        </div>
                    )}
                </div>

                {/* Analytics Overview */}
                {dashboards.length > 0 && (
                    <div className="dashboard-section">
                        <div className="dashboard-section-header">
                            <h2 className="dashboard-section-title">Analytics Overview</h2>
                            <Button 
                                tertiary
                                icon="fas fa-chart-bar"
                                onClick={handleAnalytics}
                            >
                                View Details
                            </Button>
                        </div>
                        <div className="analytics-preview">
                            {userApps.slice(0, 1).map(app => (
                                <div key={app.meta.id} className="analytics-dashboard-preview">
                                    <AnalyticsDashboard
                                        app={app}
                                        definitions={{}}
                                        filters={[]}
                                        optionalFilters={[]}
                                        searchFilters={[]}
                                        additionalMaps={{}}
                                        compactMode={true}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recent Activity */}
                <div className="dashboard-section">
                    <h2 className="dashboard-section-title">Recent Activity</h2>
                    <div className="recent-activity">
                        <div className="activity-item">
                            <div className="activity-icon">
                                <Icon name="fas fa-edit" />
                            </div>
                            <div className="activity-content">
                                <p><strong>App updated</strong></p>
                                <p className="activity-time">2 hours ago</p>
                            </div>
                        </div>
                        <div className="activity-item">
                            <div className="activity-icon">
                                <Icon name="fas fa-plus" />
                            </div>
                            <div className="activity-content">
                                <p><strong>New page created</strong></p>
                                <p className="activity-time">1 day ago</p>
                            </div>
                        </div>
                        <div className="activity-item">
                            <div className="activity-icon">
                                <Icon name="fas fa-users" />
                            </div>
                            <div className="activity-content">
                                <p><strong>User registered</strong></p>
                                <p className="activity-time">2 days ago</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}